import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, X, MoveHorizontal } from "lucide-react";
import { galleryProjects } from "@/lib/galleryData";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLanguage } from "@/i18n/LanguageContext";

export const Gallery = () => {
  const { t } = useLanguage();
  const [active, setActive] = useState(null);
  const project = active !== null ? galleryProjects[active] : null;

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-28 sm:py-40">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="max-w-2xl mb-16"
        >
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6">{t("gallery.eyebrow")}</p>
          <h2 className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl leading-tight text-white">
            {t("gallery.title")} <span className="gold-gradient-text italic">{t("gallery.titleAccent")}</span>
          </h2>
          <p className="mt-6 text-[#A0ABC0] font-light">
            {t("gallery.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {galleryProjects.map((p, i) => (
            <motion.button
              key={p.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              onClick={() => setActive(i)}
              data-testid={`gallery-item-${i}`}
              className="group relative overflow-hidden border border-[#D4AF37]/10 hover:border-[#D4AF37]/50 transition-colors duration-500 text-left"
            >
              {p.beforeAfter && (
                <span className="absolute top-4 right-4 z-10 flex items-center gap-1.5 bg-[#0A1420]/80 backdrop-blur-sm border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 pointer-events-none">
                  <MoveHorizontal size={12} /> {t("caseStudy.beforeAfterTitle")}
                </span>
              )}
              <div className="overflow-hidden">
                <img
                  src={p.cover}
                  alt={p.title}
                  className="w-full h-[420px] object-cover transition-transform duration-[900ms] ease-out group-hover:scale-125"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1420] via-[#0A1420]/25 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-7 pointer-events-none">
                <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em]">{t(`gallery.categories.${p.categoryKey}`)}</p>
                <div className="flex items-center justify-between mt-2">
                  <h3 className="font-serif-lux text-2xl text-white">{p.title}</h3>
                  <ArrowUpRight className="text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-500" size={22} />
                </div>
                <p className="text-[#A0ABC0] text-xs mt-2">{p.photos.length} {t("gallery.photos")}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={active !== null} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="gallery-dialog"
          className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto bg-[#0A1420] border border-[#D4AF37]/30 rounded-none p-0"
        >
          {project && (
            <div className="p-8 sm:p-12">
              <div className="flex items-start justify-between gap-6 mb-10">
                <div>
                  <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] mb-3">
                    {t(`gallery.categories.${project.categoryKey}`)}
                  </p>
                  <DialogTitle className="font-serif-lux text-3xl sm:text-4xl text-white">
                    {project.title}
                  </DialogTitle>
                  <DialogDescription className="mt-4 text-[#A0ABC0] font-light max-w-2xl">
                    {t(`gallery.projects.${project.key}`)}
                  </DialogDescription>
                  <Link
                    to={`/projet/${project.key}`}
                    onClick={() => setActive(null)}
                    data-testid="view-case-study"
                    className="inline-flex items-center gap-2 mt-6 text-[#D4AF37] text-sm uppercase tracking-widest border-b border-[#D4AF37]/40 pb-1 hover:border-[#D4AF37] transition-colors duration-300"
                  >
                    {t("caseStudy.viewCase")} <ArrowUpRight size={16} />
                  </Link>
                </div>
                <button
                  onClick={() => setActive(null)}
                  data-testid="gallery-dialog-close"
                  className="shrink-0 h-11 w-11 border border-white/15 flex items-center justify-center text-[#A0ABC0] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
                  aria-label="Fermer"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {project.photos.map((src, idx) => (
                  <div
                    key={src}
                    data-testid={`project-photo-${idx}`}
                    className="group overflow-hidden border border-[#D4AF37]/10 bg-[#080f18] flex items-center justify-center"
                  >
                    <img
                      src={src}
                      alt={`${project.title} — ${idx + 1}`}
                      className="w-full h-96 object-contain transition-transform duration-[900ms] ease-out group-hover:scale-110"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
